import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowLeft, Calendar, Users, Settings, ClipboardList, Package, X } from 'lucide-react';
import { toast } from 'sonner';
import { WorkInProgressSummary } from '../services/wipApi';
import { 
  workOrderOperationApiService, 
  WorkOrderOperationBulkCreateRequest,
  WorkOrderOperationStatus,
  WorkOrderOperation
} from '../services/workOrderOperationApi';
import { workOrderApiService } from '../services/workOrderApi';
import { processFlowApiService } from '../services/processFlowApi';
import { endgoodVariantApiService, EndgoodVariant } from '../services/endgoodVariantApi';
import { bomDetailApiService, BomDetail } from '../services/bomDetailApi';
import { bomHeaderApiService } from '../services/bomHeaderApi';
import { grnApiService } from '../services/grnApi';
import { formatDateTimeForAPI } from '../utils/dateUtils';
import { SHIFTS } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { getWorkOrderConfig } from '../config/uiConfig';
import { workerApiService, Worker } from '../services/workerApi';
import { physicalMachineApiService, PhysicalMachine } from '../services/physicalMachineApi';
import { materialIssueApiService, MaterialIssueType, MaterialIssueStatus, MaterialIssueCreateRequest } from '../services/materialIssueApi';

interface RawMaterial {
  id: number;
  grnId: number;
  grnNumber: string;
  heatNo: string;
  itemName: string;
  itemSku: string;
  receivedQuantity: number;
  remainingQuantity: number;
  unit: string;
  receivedDate: string;
  supplierName: string;
  rawItemId: number;
}

interface MaterialSelection {
  bomDetailId: number;
  rawItemId: number | null;
  endgoodId: number | null;
  rawCategoryId: number; // Category ID for the material
  itemName: string;
  requiredQuantity: number;
  unit: string;
  selectedGrnId: number | null;
  selectedHeatNo: string | null;
  availableMaterials: RawMaterial[];
  isInternalManufacturingPart: boolean;
}

interface OperationEntry {
  id: string;
  workOrderId: number;
  workOrderNumber: string;
  processName: string;
  nextProcessName: string;
  endgoodName: string;
  endgoodId: number; // Add endgood ID for variant lookup
  maxProductionQty: number;
  productionQty: number;
  rejectQty: number;
  date: string;
  shift: string;
  workerId: string;
  physicalMachineId: string;
  remark: string;
  processStepId?: number; // Add process step ID
  bomHeaderId?: number; // Add BOM header ID
  workAreaId?: number; // Add work area ID for fetching workers and machines
  workers?: Worker[]; // Workers available for this work area
  loadingWorkers?: boolean; // Loading state for workers
  machines?: PhysicalMachine[]; // Machines available for this work area
  loadingMachines?: boolean; // Loading state for machines
  variants?: EndgoodVariant[]; // Add variants for this endgood
  variantEntries?: Array<{
    variantId: number;
    variant: EndgoodVariant;
    productionQty: number;
    rejectQty: number;
  }>; // Add variant-specific quantities
  requiredMaterials?: MaterialSelection[]; // Materials required for next process
  loadingMaterials?: boolean; // Loading state for materials
  hasMaterialIssues?: boolean; // Flag to indicate if material issues already exist for this work order
  nextProcessSequenceOrder?: number; // Sequence order of the next process (1 = first process)
}

export default function WorkOrderOperationRegistration() {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTasks = (location.state?.selectedTasks || []) as WorkInProgressSummary[];
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  
  const [entries, setEntries] = useState<OperationEntry[]>([]);
  const [shifts] = useState(SHIFTS);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const loadedWorkersRef = useRef<Set<string>>(new Set());
  const loadedMachinesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Initialize entries for each selected task
    const initializeEntries = async () => {
      const initialEntries: OperationEntry[] = await Promise.all(
        selectedTasks.map(async (task, index) => {
          // Use bomHeaderId from task (provided by API response)
          // Fallback to fetching from work order if not present
          let bomHeaderId: number | undefined = task.bomHeaderId;
          
          if (!bomHeaderId) {
            try {
              const workOrder = await workOrderApiService.getWorkOrderById(task.workOrderId.toString());
              bomHeaderId = workOrder.data?.bomHeaderId 
                || workOrder.data?.bomHeader?.id;
            } catch (error) {
              // Failed to fetch work order
            }
          }

          // Get workAreaId directly from selected task object
          const workAreaId = task.nextProcessWorkAreaId;

          // Check if material issues already exist for this work order
          let hasMaterialIssues = false;
          try {
            const materialIssuesResponse = await materialIssueApiService.getMaterialIssuesByWorkOrder(
              task.workOrderId.toString(),
              1,
              1
            );
            hasMaterialIssues = materialIssuesResponse.success && 
                                materialIssuesResponse.data && 
                                materialIssuesResponse.data.length > 0;
          } catch (error) {
            // Failed to check material issues
          }

          return {
            id: `entry-${index}`,
            workOrderId: task.workOrderId,
            workOrderNumber: task.workOrderNumber,
            processName: task.processName,
            nextProcessName: task.nextProcessName,
            endgoodName: task.endgoodName,
            endgoodId: task.endgoodId || 0,
            maxProductionQty: task.wipQuantity,
            productionQty: 0,
            rejectQty: 0,
            date: '',
            shift: 'MORNING',
            workerId: '',
            physicalMachineId: '',
            remark: '',
            processStepId: task.nextProcessStepId,
            bomHeaderId,
            workAreaId,
            workers: [],
            loadingWorkers: false,
            machines: [],
            loadingMachines: false,
            variants: [],
            variantEntries: [],
            requiredMaterials: [],
            loadingMaterials: false,
            hasMaterialIssues
          };
        })
      );
      setEntries(initialEntries);

      // Load variants for each entry
      loadVariantsForEntries(initialEntries);
      
      // Load materials for next process
      loadMaterialsForEntries(initialEntries);
    };

    initializeEntries();
  }, [selectedTasks]);

  // Helper function to check if category is "Internal Manufacturing Part" or "Internal Manufactured Parts"
  const isInternalManufacturingParts = (categoryName: string | undefined) => {
    if (!categoryName) return false;
    const lowerName = categoryName.toLowerCase().trim();
    return lowerName === 'internal manufacturing parts' || 
           lowerName === 'internal manufactured parts' ||
           lowerName === 'internal manufacturing part' ||
           lowerName === 'internal manufactured part';
  };

  // Load materials required for next process
  const loadMaterialsForEntries = useCallback(async (entries: OperationEntry[]) => {
    const branchId = selectedBranch || user?.defaultBranchId;
    
    const updatedEntries = await Promise.all(
      entries.map(async (entry) => {
        // Skip if no BOM header or next process
        if (!entry.bomHeaderId) {
          return entry;
        }
        
        if (!entry.nextProcessName) {
          return entry;
        }

        try {
          // Set loading state
          setEntries(prev => prev.map(e => 
            e.id === entry.id ? { ...e, loadingMaterials: true } : e
          ));

          // Get BOM header to access process flow
          const bomHeader = await bomHeaderApiService.getBomHeaderById(entry.bomHeaderId.toString());
          
          // Check if next process is the first process
          let nextProcessSequenceOrder: number | undefined = undefined;
          if (entry.processStepId && bomHeader.processFlow?.id) {
            try {
              const processSteps = await processFlowApiService.getProcessStepsByProcessFlow(bomHeader.processFlow.id);
              const nextProcessStep = processSteps.find(step => step.id === entry.processStepId);
              if (nextProcessStep) {
                nextProcessSequenceOrder = nextProcessStep.sequenceOrder;
                console.log('🔍 Next process sequence order:', {
                  processStepId: entry.processStepId,
                  nextProcessName: entry.nextProcessName,
                  sequenceOrder: nextProcessSequenceOrder,
                  isFirstProcess: nextProcessSequenceOrder === 1
                });
              }
            } catch (error) {
              console.error('Error fetching process steps:', error);
              // Failed to fetch process steps, continue without sequence order check
            }
          } else {
            console.log('⚠️ Cannot determine sequence order:', {
              processStepId: entry.processStepId,
              processFlowId: bomHeader.processFlow?.id
            });
          }

          // Get all BOM details for this BOM header
          const bomDetails = await bomDetailApiService.getBomDetailsByHeader(entry.bomHeaderId);
          
          // Filter BOM details for the next process - try case-insensitive matching
          const nextProcessMaterials = bomDetails.filter(detail => {
            const processName = detail.process?.processName;
            const nextProcessName = entry.nextProcessName;
            return processName && processName.toLowerCase().trim() === nextProcessName.toLowerCase().trim();
          });

          if (nextProcessMaterials.length === 0) {
            // No materials required for next process
            return {
              ...entry,
              requiredMaterials: [],
              loadingMaterials: false,
              nextProcessSequenceOrder
            };
          }

          // Separate materials into raw items and endgoods (internal manufacturing parts)
          const rawItemMaterials = nextProcessMaterials.filter(detail => {
            const categoryName = detail.rawCategory?.categoryName || detail.rawItem?.itemCategory?.categoryName;
            return !isInternalManufacturingParts(categoryName) && detail.rawItem;
          });

          const endgoodMaterials = nextProcessMaterials.filter(detail => {
            const categoryName = detail.rawCategory?.categoryName || detail.rawItem?.itemCategory?.categoryName;
            return isInternalManufacturingParts(categoryName) && detail.endgood;
          });

          // Fetch GRN details only for raw items (not for endgoods)
          let availableMaterials: RawMaterial[] = [];
          const rawItemUnitMap = new Map<number, string>();
          
          if (rawItemMaterials.length > 0) {
          // Get raw item IDs for required materials
            const rawItemIds = rawItemMaterials.map(detail => detail.rawItem!.id);
          
          // Fetch GRN details for these raw items
          const grnDetailList = await grnApiService.searchGRNDetailsByRawItems(rawItemIds, branchId) as any[];
          
            // Build unit map for raw items
          bomDetails.forEach(bomDetail => {
              if (bomDetail.rawItem) {
            rawItemUnitMap.set(bomDetail.rawItem.id, bomDetail.unit || bomDetail.rawItem.itemUnit || '');
              }
          });

          // Filter GRNs with remaining quantity > minimum threshold
          const workOrderConfig = getWorkOrderConfig();
          const minRemainingQty = workOrderConfig.minRemainingQuantity;
          
          const filteredGrnDetails = grnDetailList.filter((detail: any) => {
            const remainingQty = detail.remainingQuantity || 0;
            return remainingQty > minRemainingQty;
          });
          
          for (let index = 0; index < filteredGrnDetails.length; index++) {
            const detail = filteredGrnDetails[index] as any;
            const unit = rawItemUnitMap.get(detail.rawItemId) || '';
            
            const uniqueId = detail.rawItemId * 10000 + index;
              
              // Get GRN ID from detail
              const grnId = detail.grn?.id || detail.grnId || 0;
            
            availableMaterials.push({
              id: uniqueId,
                grnId: grnId,
                grnNumber: detail.grnNumber || detail.grn?.grnNumber,
              heatNo: detail.heatNo || 'N/A',
                itemName: detail.itemName || detail.rawItem?.itemName,
                itemSku: detail.rawItemSku || detail.rawItem?.itemSku,
                receivedQuantity: detail.originalQuantity || detail.receivedQuantity,
              remainingQuantity: detail.remainingQuantity,
              unit: unit,
                receivedDate: detail.grnDate || detail.grn?.grnDate,
                supplierName: detail.supplierName || detail.grn?.supplier?.name || 'N/A',
                rawItemId: detail.rawItemId || detail.rawItem?.id
              });
            }
          }

          // Create material selections for raw items
          const rawItemSelections: MaterialSelection[] = rawItemMaterials.map(bomDetail => {
            const itemMaterials = availableMaterials.filter(rm => rm.rawItemId === bomDetail.rawItem!.id);
            
            // Get rawCategoryId
            const rawCategoryId = bomDetail.rawCategoryId || bomDetail.rawCategory?.id || bomDetail.rawItem?.itemCategory?.id || 0;
            
            return {
              bomDetailId: bomDetail.id,
              rawItemId: bomDetail.rawItem!.id,
              endgoodId: null,
              rawCategoryId: rawCategoryId,
              itemName: bomDetail.rawItem!.itemName,
              requiredQuantity: bomDetail.quantity,
              unit: bomDetail.unit || bomDetail.rawItem!.itemUnit || '',
              selectedGrnId: null,
              selectedHeatNo: null,
              availableMaterials: itemMaterials,
              isInternalManufacturingPart: false
            };
          });

          // Create material selections for endgoods (internal manufacturing parts)
          const endgoodSelections: MaterialSelection[] = endgoodMaterials.map(bomDetail => {
            // Get rawCategoryId
            const rawCategoryId = bomDetail.rawCategoryId || bomDetail.rawCategory?.id || bomDetail.rawItem?.itemCategory?.id || 0;
            
            return {
              bomDetailId: bomDetail.id,
              rawItemId: null,
              endgoodId: bomDetail.endgood!.id,
              rawCategoryId: rawCategoryId,
              itemName: bomDetail.endgood!.endgoodName,
              requiredQuantity: bomDetail.quantity,
              unit: bomDetail.unit || 'pcs',
              selectedGrnId: null,
              selectedHeatNo: null,
              availableMaterials: [], // No GRN materials for endgoods
              isInternalManufacturingPart: true
            };
          });

          const materialSelections = [...rawItemSelections, ...endgoodSelections];

          console.log('📦 Materials loaded:', {
            nextProcessName: entry.nextProcessName,
            nextProcessSequenceOrder,
            rawItemMaterialsCount: rawItemMaterials.length,
            endgoodMaterialsCount: endgoodMaterials.length,
            totalMaterialsCount: materialSelections.length,
            shouldShowSection: nextProcessSequenceOrder !== 1 && materialSelections.length > 0
          });

          return {
            ...entry,
            requiredMaterials: materialSelections,
            loadingMaterials: false,
            nextProcessSequenceOrder
          };
        } catch (error) {
          return {
            ...entry,
            requiredMaterials: [],
            loadingMaterials: false,
            nextProcessSequenceOrder: undefined
          };
        }
      })
    );

    // Use functional update to preserve workers and machines that may have been loaded
    setEntries(prev => {
      const updated = prev.map(prevEntry => {
        const updatedEntry = updatedEntries.find(e => e.id === prevEntry.id);
        if (updatedEntry) {
          // Preserve workers, machines, and other properties that might have been set after materials loading started
          return {
            ...updatedEntry,
            workers: prevEntry.workers || updatedEntry.workers,
            machines: prevEntry.machines || updatedEntry.machines,
            loadingWorkers: prevEntry.loadingWorkers,
            loadingMachines: prevEntry.loadingMachines,
            variants: prevEntry.variants || updatedEntry.variants,
            variantEntries: prevEntry.variantEntries || updatedEntry.variantEntries,
            nextProcessSequenceOrder: updatedEntry.nextProcessSequenceOrder // Preserve sequence order from materials loading
          };
        }
        return prevEntry;
      });
      return updated;
    });
  }, [selectedBranch, user?.defaultBranchId]);

  // Load workers for each entry based on workAreaId
  // Optimized to make only one API call per unique workAreaId
  const loadWorkersForEntries = useCallback(async (entriesToLoad: OperationEntry[]) => {
    const branchId = selectedBranch || user?.defaultBranchId;
    // Filter entries that have workAreaId
    const entriesWithWorkArea = entriesToLoad.filter(e => e.workAreaId);
    
    if (entriesWithWorkArea.length === 0) {
      return;
    }
    
    // Group entries by unique workAreaId
    const workAreaMap = new Map<number, OperationEntry[]>();
    entriesWithWorkArea.forEach(entry => {
      if (entry.workAreaId) {
        const existing = workAreaMap.get(entry.workAreaId) || [];
        existing.push(entry);
        workAreaMap.set(entry.workAreaId, existing);
      }
    });
    
    const uniqueWorkAreaIds = Array.from(workAreaMap.keys());
    
    // Set loading state for entries being loaded
    const entryIds = new Set(entriesWithWorkArea.map(e => e.id));
    setEntries(prev => prev.map(e => 
      entryIds.has(e.id) && e.workAreaId ? { ...e, loadingWorkers: true } : e
    ));
    
    // Fetch workers for each unique workAreaId (one API call per workAreaId)
    const workAreaWorkersMap = new Map<number, Worker[]>();
    
    await Promise.all(
      uniqueWorkAreaIds.map(async (workAreaId) => {
        try {
          const workers = await workerApiService.getWorkersByWorkAreaId(workAreaId, branchId);
          // Ensure workers is an array
          const workersArray = Array.isArray(workers) ? workers : [];
          workAreaWorkersMap.set(workAreaId, workersArray);
        } catch (error) {
          workAreaWorkersMap.set(workAreaId, []);
        }
      })
    );
    
    // Update all entries with the fetched workers
    setEntries(prev => {
      const updated = prev.map(entry => {
        if (entryIds.has(entry.id) && entry.workAreaId) {
          const workers = workAreaWorkersMap.get(entry.workAreaId) || [];
          return {
            ...entry,
            workers,
            loadingWorkers: false
          };
        }
        return entry;
      });
      return updated;
    });
  }, [selectedBranch, user?.defaultBranchId]);

  // Load machines for each entry based on workAreaId
  // Optimized to make only one API call per unique workAreaId
  const loadMachinesForEntries = useCallback(async (entriesToLoad: OperationEntry[]) => {
    const branchId = selectedBranch || user?.defaultBranchId;
    // Filter entries that have workAreaId
    const entriesWithWorkArea = entriesToLoad.filter(e => e.workAreaId);
    
    if (entriesWithWorkArea.length === 0) {
      return;
    }
    
    // Group entries by unique workAreaId
    const workAreaMap = new Map<number, OperationEntry[]>();
    entriesWithWorkArea.forEach(entry => {
      if (entry.workAreaId) {
        const existing = workAreaMap.get(entry.workAreaId) || [];
        existing.push(entry);
        workAreaMap.set(entry.workAreaId, existing);
      }
    });
    
    const uniqueWorkAreaIds = Array.from(workAreaMap.keys());
    
    // Set loading state for entries being loaded
    const entryIds = new Set(entriesWithWorkArea.map(e => e.id));
    setEntries(prev => prev.map(e => 
      entryIds.has(e.id) && e.workAreaId ? { ...e, loadingMachines: true } : e
    ));
    
    // Fetch machines for each unique workAreaId (one API call per workAreaId)
    const workAreaMachinesMap = new Map<number, PhysicalMachine[]>();
    
    await Promise.all(
      uniqueWorkAreaIds.map(async (workAreaId) => {
        try {
          const machines = await physicalMachineApiService.getMachinesByWorkAreaId(workAreaId, branchId);
          // Ensure machines is an array
          const machinesArray = Array.isArray(machines) ? machines : [];
          workAreaMachinesMap.set(workAreaId, machinesArray);
        } catch (error) {
          workAreaMachinesMap.set(workAreaId, []);
        }
      })
    );
    
    // Update all entries with the fetched machines
    setEntries(prev => {
      const updated = prev.map(entry => {
        if (entryIds.has(entry.id) && entry.workAreaId) {
          const machines = workAreaMachinesMap.get(entry.workAreaId) || [];
          return {
            ...entry,
            machines,
            loadingMachines: false
          };
        }
        return entry;
      });
      return updated;
    });
  }, [selectedBranch, user?.defaultBranchId]);

  // Load workers and machines when entries are initialized with workAreaId
  useEffect(() => {
    const entriesNeedingWorkers = entries.filter(
      entry => {
        const key = `${entry.id}-${entry.workAreaId}-${selectedBranch || user?.defaultBranchId || 'all'}`;
        return entry.workAreaId && 
               (!entry.workers || entry.workers.length === 0) && 
               !entry.loadingWorkers &&
               !loadedWorkersRef.current.has(key);
      }
    );
    
    const entriesNeedingMachines = entries.filter(
      entry => {
        const key = `${entry.id}-${entry.workAreaId}-${selectedBranch || user?.defaultBranchId || 'all'}`;
        return entry.workAreaId && 
               (!entry.machines || entry.machines.length === 0) && 
               !entry.loadingMachines &&
               !loadedMachinesRef.current.has(key);
      }
    );
    
    if (entriesNeedingWorkers.length > 0) {
      entriesNeedingWorkers.forEach(entry => {
        loadedWorkersRef.current.add(`${entry.id}-${entry.workAreaId}-${selectedBranch || user?.defaultBranchId || 'all'}`);
      });
      loadWorkersForEntries(entriesNeedingWorkers);
    }
    
    if (entriesNeedingMachines.length > 0) {
      entriesNeedingMachines.forEach(entry => {
        loadedMachinesRef.current.add(`${entry.id}-${entry.workAreaId}-${selectedBranch || user?.defaultBranchId || 'all'}`);
      });
      loadMachinesForEntries(entriesNeedingMachines);
    }
    // Only depend on entry IDs and workAreaIds, not the full entries array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.map(e => `${e.id}-${e.workAreaId}`).join(','), selectedBranch, user?.defaultBranchId]);

  // Load variants for all entries
  const loadVariantsForEntries = async (entries: OperationEntry[]) => {
    setLoadingVariants(true);
    try {
      const updatedEntries = await Promise.all(
        entries.map(async (entry) => {
          if (entry.endgoodId > 0) {
            try {
              const variants = await endgoodVariantApiService.getVariantsByEndgoodId(entry.endgoodId);
              const variantEntries = variants.map(variant => ({
                variantId: variant.id,
                variant: variant,
                productionQty: 0,
                rejectQty: 0
              }));
              
              return {
                ...entry,
                variants,
                variantEntries
              };
            } catch (error) {
              return entry;
            }
          }
          return entry;
        })
      );
      // Use functional update to preserve workers and machines that may have been loaded
      setEntries(prev => {
        const updated = prev.map(prevEntry => {
          const updatedEntry = updatedEntries.find(e => e.id === prevEntry.id);
          if (updatedEntry) {
            // Preserve workers, machines, and other properties that might have been set after variants loading started
            return {
              ...updatedEntry,
              workers: prevEntry.workers || updatedEntry.workers,
              machines: prevEntry.machines || updatedEntry.machines,
              loadingWorkers: prevEntry.loadingWorkers,
              loadingMachines: prevEntry.loadingMachines,
              requiredMaterials: prevEntry.requiredMaterials || updatedEntry.requiredMaterials,
              loadingMaterials: prevEntry.loadingMaterials
            };
          }
          return prevEntry;
        });
        return updated;
      });
    } catch (error) {
      // Error loading variants
    } finally {
      setLoadingVariants(false);
    }
  };

  const updateEntry = (entryId: string, field: keyof OperationEntry, value: string | number) => {
    setEntries(prev => prev.map(entry => 
      entry.id === entryId ? { ...entry, [field]: value } : entry
    ));
  };

  const updateVariantEntry = (entryId: string, variantId: number, field: 'productionQty' | 'rejectQty', value: number) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === entryId) {
        const updatedVariantEntries = entry.variantEntries?.map(variantEntry => 
          variantEntry.variantId === variantId 
            ? { ...variantEntry, [field]: value }
            : variantEntry
        ) || [];
        
        // Calculate total production and reject quantities
        const totalProductionQty = updatedVariantEntries.reduce((sum, ve) => sum + ve.productionQty, 0);
        const totalRejectQty = updatedVariantEntries.reduce((sum, ve) => sum + ve.rejectQty, 0);
        
        return {
          ...entry,
          variantEntries: updatedVariantEntries,
          productionQty: totalProductionQty,
          rejectQty: totalRejectQty
        };
      }
      return entry;
    }));
  };

  const updateMaterialSelection = (entryId: string, bomDetailId: number, grnId: number, heatNo: string) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === entryId && entry.requiredMaterials) {
        const updatedMaterials = entry.requiredMaterials.map(material =>
          material.bomDetailId === bomDetailId
            ? { ...material, selectedGrnId: grnId, selectedHeatNo: heatNo }
            : material
        );
        return {
          ...entry,
          requiredMaterials: updatedMaterials
        };
      }
      return entry;
    }));
  };



  const handleSubmit = async () => {
    if (entries.length === 0) {
      toast.error('No operations to register');
      return;
    }

    // Validate all entries
    const validationErrors: string[] = [];
    entries.forEach((entry, index) => {
      if (!entry.productionQty || entry.productionQty <= 0) {
        validationErrors.push(`Entry ${index + 1}: Production quantity is required and must be greater than 0`);
      }
      
      // Validate that production + reject quantity doesn't exceed max quantity
      const totalQty = entry.productionQty + entry.rejectQty;
      if (totalQty > entry.maxProductionQty) {
        validationErrors.push(`Entry ${index + 1}: Total quantity (${totalQty}) exceeds maximum quantity (${entry.maxProductionQty})`);
      }
      
      if (!entry.date) {
        validationErrors.push(`Entry ${index + 1}: Date is required`);
      }
      if (!entry.shift) {
        validationErrors.push(`Entry ${index + 1}: Shift is required`);
      }
      if (!entry.workerId) {
        validationErrors.push(`Entry ${index + 1}: Worker is required`);
      }
      if (!entry.physicalMachineId) {
        validationErrors.push(`Entry ${index + 1}: Machine is required`);
      }
    });

    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }

    setIsLoading(true);
    try {
      // Convert entries to API format - combine variant data in single operation
      const operationsData: WorkOrderOperationBulkCreateRequest[] = entries.map(entry => {
        const selectedWorker = entry.workers?.find(w => w.id.toString() === entry.workerId);
        const selectedMachine = entry.machines?.find(m => m.id.toString() === entry.physicalMachineId);
        
        // Base operation data
        const baseOperation = {
          operationName: entry.nextProcessName,
          operationDescription: `Production entry for ${entry.endgoodName}`,
          workCenter: 'Production Floor', // Default work center
          machineCode: entry.physicalMachineId, // Use physical machine ID
          quantityCompleted: Number(entry.productionQty),
          quantityRejected: Number(entry.rejectQty),
          rejectionReason: Number(entry.rejectQty) > 0 ? entry.remark : undefined,
          status: WorkOrderOperationStatus.IN_PROGRESS,
          startTime: formatDateTimeForAPI(new Date(entry.date)),
          endTime: formatDateTimeForAPI(new Date(entry.date)), // Same as start time for now
          productionDate: entry.date, // Use the date as production date
          assignedOperator: selectedWorker?.name || entry.workerId,
          qualityCheckStatus: 'PENDING',
          qualityCheckNotes: entry.remark || undefined,
          workOrderId: entry.workOrderId,
          processStepId: entry.processStepId, // Use the process step ID from the entry
          isActive: true,
          createdBy: user?.id,
          updatedBy: user?.id
        };

        // If variants exist, add variant data as JSON object
        if (entry.variants && entry.variants.length > 0 && entry.variantEntries && entry.variantEntries.length > 0) {
          const variantData: { [variantId: number]: { productionQty: number; rejectQty: number } } = {};
          
          entry.variantEntries.forEach(variantEntry => {
            if (variantEntry.productionQty > 0 || variantEntry.rejectQty > 0) {
              variantData[variantEntry.variantId] = {
                productionQty: variantEntry.productionQty,
                rejectQty: variantEntry.rejectQty
              };
            }
          });

          // Only add variant data if there are actual quantities
          if (Object.keys(variantData).length > 0) {
            return {
              ...baseOperation,
              variantData
            };
          }
        }

        // No variants or no variant quantities, return base operation
        return baseOperation;
      });

      // Call API to register operations
      const response = await workOrderOperationApiService.registerWorkOrderOperations(operationsData);
      
      if (response.success && response.data) {
        // Map created operations to entries by workOrderId and processStepId
        const createdOperations = response.data;
        const operationMap = new Map<string, WorkOrderOperation>();
        
        createdOperations.forEach(operation => {
          // Create a key from workOrderId and processStepId to match with entries
          // Handle both nested workOrder object and direct workOrderId property
          const workOrderId = operation.workOrder?.id || (operation as any).workOrderId;
          const processStepId = operation.processStep?.id || (operation as any).processStepId;
          const key = `${workOrderId}_${processStepId || 'no-process'}`;
          operationMap.set(key, operation);
        });

        // Validate material quantities before creating material issues
        const materialValidationErrors: string[] = [];
        entries.forEach(entry => {
          if (entry.requiredMaterials && entry.requiredMaterials.length > 0) {
            entry.requiredMaterials.forEach(material => {
              // Skip validation for internal manufacturing parts (endgoods)
              if (material.isInternalManufacturingPart) {
                return;
              }
              
              // Only validate if heat no is selected
              if (material.selectedHeatNo) {
                // Calculate issue quantity = production qty * (required material for 1 qty)
                const issueQty = entry.productionQty * material.requiredQuantity;
                
                // Find the selected material to get remaining quantity
                const selectedMaterial = material.availableMaterials.find(m => m.heatNo === material.selectedHeatNo);
                if (selectedMaterial) {
                  // Check if calculated issue quantity exceeds remaining quantity
                  if (issueQty > selectedMaterial.remainingQuantity) {
                    materialValidationErrors.push(
                      `Work Order ${entry.workOrderNumber}: ${material.itemName} (Heat: ${material.selectedHeatNo}) - ` +
                      `Required: ${issueQty.toFixed(2)} ${material.unit}, ` +
                      `Available: ${selectedMaterial.remainingQuantity} ${material.unit}`
                    );
                  }
                }
              }
            });
          }
        });

        // If there are validation errors, show them and prevent submission
        if (materialValidationErrors.length > 0) {
          materialValidationErrors.forEach(error => toast.error(error));
          setIsLoading(false);
          return;
        }

        // Create material issues for all selected materials (bulk create)
        const materialIssuesData: MaterialIssueCreateRequest[] = [];
        
        entries.forEach(entry => {
          if (entry.requiredMaterials && entry.requiredMaterials.length > 0) {
            // Find the corresponding created operation for this entry
            const operationKey = `${entry.workOrderId}_${entry.processStepId || 'no-process'}`;
            const createdOperation = operationMap.get(operationKey);
            
            entry.requiredMaterials.forEach(material => {
              // Skip material issue creation for internal manufacturing parts (endgoods)
              if (material.isInternalManufacturingPart) {
                return;
              }
              
              // Only create material issue if heat no is selected
              if (material.selectedHeatNo) {
                // Calculate issue quantity = production qty * (required material for 1 qty)
                const issueQty = entry.productionQty * material.requiredQuantity;
                const issueDate = entry.date; // Use production date as issue date
                
                // Build material issue data based on whether it's internal manufactured part
                const materialIssueData: MaterialIssueCreateRequest = {
                  issueDate,
                  issueQuantity: issueQty,
                  consumedQuantity: issueQty, // Consumption is same as production date
                  returnedQuantity: 0,
                  wastedQuantity: 0,
                  issueType: MaterialIssueType.PRODUCTION,
                  status: MaterialIssueStatus.ISSUED,
                  issueReason: `Material issued for production - ${entry.endgoodName}`,
                  issuedBy: user?.username || 'System',
                  receivedBy: 'Production',
                  heatNumber: material.selectedHeatNo,
                  lotNumber: entry.workOrderNumber, // Lot number = work order number
                  notes: `Material issued for work order ${entry.workOrderNumber}. Issue quantity: ${issueQty} ${material.unit}`,
                  branchId: selectedBranch || user?.defaultBranchId,
                  workOrderId: entry.workOrderId,
                  workOrderOperationId: createdOperation?.id, // Use the created operation ID
                  rawCategoryId: material.rawCategoryId,
                  // If internal manufactured parts, use endgoodId; otherwise use rawItemId
                  ...(material.isInternalManufacturingPart
                    ? { endgoodId: material.endgoodId || 0 }
                    : { rawItemId: material.rawItemId || 0 }
                  )
                };

                materialIssuesData.push(materialIssueData);
              }
            });
          }
        });

        // Create all material issues in bulk
        if (materialIssuesData.length > 0) {
          try {
            const bulkResponse = await materialIssueApiService.createBulkMaterialIssues(materialIssuesData);
            if (!bulkResponse.success || !bulkResponse.data) {
              toast.error(`Failed to create some material issues`);
            }
          } catch (error) {
            toast.error(`Error creating material issues`);
          }
        }

        toast.success(`Successfully registered ${entries.length} operations!`);
        navigate('/wip-tracking');
      } else {
        toast.error(response.message || 'Failed to register operations');
      }
    } catch (error) {
      toast.error('Failed to register operations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/wip-tracking');
  };

  const handleDeleteEntry = (entryId: string) => {
    const entry = entries.find(e => e.id === entryId);
    if (!entry) return;

    if (window.confirm(`Are you sure you want to remove work order ${entry.workOrderNumber} from the registration list?`)) {
      // Remove entry from state
      setEntries(prev => prev.filter(e => e.id !== entryId));
      
      // Clean up refs for workers and machines
      if (entry.workAreaId) {
        const key = `${entryId}-${entry.workAreaId}-${selectedBranch || user?.defaultBranchId || 'all'}`;
        loadedWorkersRef.current.delete(key);
        loadedMachinesRef.current.delete(key);
      }
      
      toast.success(`Work order ${entry.workOrderNumber} removed from registration list`);
    }
  };

  if (selectedTasks.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
          <CardContent className="p-8 text-center">
            <ClipboardList className="h-16 w-16 text-primary-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-primary-800 mb-2">No Work Orders Selected</h2>
            <p className="text-primary-600 mb-6">Please select work orders from the WIP tracking page to register operations.</p>
            <Button 
              onClick={handleCancel} 
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to WIP Tracking
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
          Work Order Operation Registration
        </h1>
        <p className="text-primary-600">Register production entries for {entries.length} selected work orders</p>
      </div>

      {/* Navigation and Actions */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to WIP Tracking
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Operation Entries */}
      <div className="space-y-4">
        {entries.map((entry, index) => (
          <Card key={entry.id} className="border-primary-200 shadow-lg shadow-primary-100/50">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200 py-2">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-4 flex-1">
                  <CardTitle className="text-lg text-primary-800 m-0">
                        Work Order: {entry.workOrderNumber}
                      </CardTitle>
                      <Badge variant="outline" className="border-blue-200 text-blue-700 bg-blue-50">
                        Next Process: {entry.nextProcessName}
                      </Badge>
                      <Badge variant="outline" className="border-green-200 text-green-700 bg-green-50">
                        Endgood: {entry.endgoodName}
                      </Badge>
                    </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <span className="text-sm text-primary-600 font-medium whitespace-nowrap">Max Production Qty:</span>
                  <span className="text-lg font-bold text-primary-800 whitespace-nowrap">
                    {entry.maxProductionQty}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteEntry(entry.id)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                    title="Remove this work order"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-4">
              <div className="space-y-4">
                {/* Production Info Row - Compact Layout */}
                <div className="space-y-4">
                  {/* Production Qty - Show variants if available, otherwise show simple input */}
                  {loadingVariants ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mr-2"></div>
                      <span className="text-sm text-gray-600">Loading variants...</span>
                    </div>
                  ) : entry.variants && entry.variants.length > 0 ? (
                    <div className="space-y-3">
                      <Label className="text-primary-600 font-medium text-sm">Production & Reject Qty by Variant</Label>
                      
                      {/* Variants Table - Compact */}
                      <div className="bg-gray-50 rounded-lg border overflow-hidden">
                        <div className="grid grid-cols-12 gap-2 p-2 bg-gray-100 text-xs font-medium text-gray-600">
                          <div className="col-span-4">Variant</div>
                          <div className="col-span-3 text-center">Production</div>
                          <div className="col-span-3 text-center">Reject</div>
                          <div className="col-span-2 text-center">Total</div>
                        </div>
                        {entry.variantEntries?.map((variantEntry) => (
                          <div key={variantEntry.variantId} className="grid grid-cols-12 gap-2 p-2 border-b border-gray-200 last:border-b-0">
                            <div className="col-span-4 flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                                style={{ backgroundColor: variantEntry.variant.color?.toLowerCase() || '#cccccc' }}
                              />
                              <span className="text-sm font-medium text-gray-700 truncate">
                                {variantEntry.variant.color}
                              </span>
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                value={variantEntry.productionQty || ''}
                                onChange={(e) => updateVariantEntry(entry.id, variantEntry.variantId, 'productionQty', parseInt(e.target.value) || 0)}
                                className="h-8 text-center text-sm border-gray-300 focus:border-primary-400 focus:ring-primary-400"
                                min="0"
                                placeholder="0"
                              />
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                value={variantEntry.rejectQty || ''}
                                onChange={(e) => updateVariantEntry(entry.id, variantEntry.variantId, 'rejectQty', parseInt(e.target.value) || 0)}
                                className="h-8 text-center text-sm border-gray-300 focus:border-primary-400 focus:ring-primary-400"
                                min="0"
                                placeholder="0"
                              />
                            </div>
                            <div className="col-span-2 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {variantEntry.productionQty + variantEntry.rejectQty}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Totals - Compact */}
                      <div className="flex justify-between items-center bg-blue-50 px-3 py-2 rounded border border-blue-200">
                        <div className="flex gap-4 text-sm">
                          <span className="text-blue-700 font-medium">Total Production: <span className="text-blue-800 font-bold">{entry.productionQty}</span></span>
                          <span className="text-blue-700 font-medium">Total Reject: <span className="text-blue-800 font-bold">{entry.rejectQty}</span></span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Max: {entry.maxProductionQty} | Used: {entry.productionQty + entry.rejectQty}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Production Qty */}
                      <div>
                        <Label className="text-primary-600 font-medium text-sm">Production Qty *</Label>
                        <Input
                          value={entry.productionQty}
                          onChange={(e) => updateEntry(entry.id, 'productionQty', parseInt(e.target.value) || 0)}
                          className="mt-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                          required
                          placeholder="Enter quantity"
                        />
                        {(() => {
                          const totalQty = entry.productionQty + entry.rejectQty;
                          const isValid = totalQty <= entry.maxProductionQty;
                          
                          return (
                            <div className="mt-1 text-xs text-gray-500">
                              Max: {entry.maxProductionQty} | Total: {totalQty} 
                              {!isValid && (
                                <span className="text-red-600 ml-2">
                                  (Exceeds by {totalQty - entry.maxProductionQty})
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Reject Qty */}
                      <div>
                        <Label className="text-primary-600 font-medium text-sm">Reject Qty</Label>
                        <Input
                          value={entry.rejectQty}
                          onChange={(e) => updateEntry(entry.id, 'rejectQty', parseInt(e.target.value) || 0)}
                          className="mt-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                          placeholder="Enter reject quantity"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Operation Details Row - Compact */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Date */}
                  <div>
                    <Label className="flex items-center gap-2 text-primary-600 font-medium">
                      <Calendar className="h-4 w-4 text-primary-500" />
                      Date *
                    </Label>
                    <Input
                      type="date"
                      value={entry.date}
                      onChange={(e) => updateEntry(entry.id, 'date', e.target.value)}
                      className="mt-2 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                      required
                    />
                  </div>

                  {/* Shift */}
                  <div>
                    <Label className="text-primary-600 font-medium">Shift *</Label>
                    <Select 
                      value={entry.shift} 
                      onValueChange={(value) => updateEntry(entry.id, 'shift', value)}
                    >
                      <SelectTrigger className="mt-2 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                        <SelectValue placeholder="Select Shift" />
                      </SelectTrigger>
                      <SelectContent>
                        {shifts.map(shift => (
                          <SelectItem key={shift} value={shift}>
                            {shift}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Select Worker */}
                  <div>
                    <Label className="flex items-center gap-2 text-primary-600 font-medium">
                      <Users className="h-4 w-4 text-primary-500" />
                      Select Worker *
                    </Label>
                    <Select 
                      value={entry.workerId} 
                      onValueChange={(value) => updateEntry(entry.id, 'workerId', value)}
                      disabled={entry.loadingWorkers}
                    >
                      <SelectTrigger className="mt-2 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                        <SelectValue placeholder={entry.loadingWorkers ? "Loading workers..." : "Select Worker"} />
                      </SelectTrigger>
                      <SelectContentWithSearch searchPlaceholder="Search workers...">
                        {(() => {
                          if (entry.loadingWorkers) {
                            return <SelectItem value="loading" disabled>Loading workers...</SelectItem>;
                          }
                          
                          if (!entry.workers || entry.workers.length === 0) {
                            return <SelectItem value="no-workers" disabled>No workers available</SelectItem>;
                          }
                          
                          return entry.workers.map(worker => (
                            <SelectItem key={worker.id} value={worker.id.toString()}>
                            {worker.name}
                          </SelectItem>
                          ));
                        })()}
                      </SelectContentWithSearch>
                    </Select>
                  </div>

                  {/* Select Physical Machine */}
                  <div>
                    <Label className="flex items-center gap-2 text-primary-600 font-medium">
                      <Settings className="h-4 w-4 text-primary-500" />
                      Select Machine *
                    </Label>
                    <Select 
                      value={entry.physicalMachineId} 
                      onValueChange={(value) => updateEntry(entry.id, 'physicalMachineId', value)}
                      disabled={entry.loadingMachines}
                    >
                      <SelectTrigger className="mt-2 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                        <SelectValue placeholder={entry.loadingMachines ? "Loading machines..." : "Select Machine"} />
                      </SelectTrigger>
                      <SelectContentWithSearch searchPlaceholder="Search machines...">
                        {(() => {
                          if (entry.loadingMachines) {
                            return <SelectItem value="loading" disabled>Loading machines...</SelectItem>;
                          }
                          
                          if (!entry.machines || entry.machines.length === 0) {
                            return <SelectItem value="no-machines" disabled>No machines available</SelectItem>;
                          }
                          
                          return entry.machines.map(machine => (
                            <SelectItem key={machine.id} value={machine.id.toString()}>
                              {machine.description || machine.machineName}
                          </SelectItem>
                          ));
                        })()}
                      </SelectContentWithSearch>
                    </Select>
                  </div>
                </div>

                {/* Remark - Compact */}
                <div>
                  <Label className="text-primary-600 font-medium text-sm">Remark</Label>
                  <Input
                    value={entry.remark}
                    onChange={(e) => updateEntry(entry.id, 'remark', e.target.value)}
                    className="mt-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    placeholder="Enter any remarks..."
                  />
                </div>

                {/* Material Selection for Next Process */}
                {/* Show if: next process is not the first process AND it requires materials */}
                {(() => {
                  const shouldShow = entry.bomHeaderId && 
                    entry.nextProcessName && 
                    entry.requiredMaterials && 
                    entry.requiredMaterials.length > 0 &&
                    entry.nextProcessSequenceOrder !== undefined &&
                    entry.nextProcessSequenceOrder !== 1; // Not the first process
                  
                  if (entry.bomHeaderId && entry.nextProcessName) {
                    console.log('🔍 Material section visibility check:', {
                      workOrderNumber: entry.workOrderNumber,
                      nextProcessName: entry.nextProcessName,
                      bomHeaderId: entry.bomHeaderId,
                      hasMaterialIssues: entry.hasMaterialIssues,
                      requiredMaterialsCount: entry.requiredMaterials?.length || 0,
                      nextProcessSequenceOrder: entry.nextProcessSequenceOrder,
                      shouldShow: shouldShow,
                      conditions: {
                        hasBomHeader: !!entry.bomHeaderId,
                        hasNextProcess: !!entry.nextProcessName,
                        hasMaterials: !!(entry.requiredMaterials && entry.requiredMaterials.length > 0),
                        notFirstProcess: entry.nextProcessSequenceOrder !== undefined && entry.nextProcessSequenceOrder !== 1
                      }
                    });
                  }
                  
                  return shouldShow;
                })() && (
                  <div className="mt-6 pt-6 border-t border-primary-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Package className="h-5 w-5 text-primary-600" />
                      <Label className="text-primary-600 font-medium text-base">
                        Material Selection for Next Process: {entry.nextProcessName}
                      </Label>
                    </div>
                    {entry.loadingMaterials ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mr-2"></div>
                        <span className="text-sm text-gray-600">Loading materials...</span>
                      </div>
                    ) : entry.requiredMaterials && entry.requiredMaterials.length > 0 ? (
                      <div className="space-y-4">
                        {entry.requiredMaterials.map((material) => (
                          <Card key={material.bomDetailId} className="border-blue-200 bg-blue-50/30">
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <Label className="text-blue-800 font-semibold">{material.itemName}</Label>
                                    <p className="text-sm text-gray-600">
                                      Required: {material.requiredQuantity} {material.unit}
                                    </p>
                                  </div>
                                </div>
                                
                                {material.isInternalManufacturingPart ? (
                                  <div className="p-3 bg-purple-50 rounded border border-purple-200">
                                    <p className="text-sm text-purple-700">
                                      Internal Manufacturing Part: {material.itemName}
                                    </p>
                                    <p className="text-xs text-purple-600 mt-1">
                                      No GRN selection needed for internal manufacturing parts
                                    </p>
                                  </div>
                                ) : material.availableMaterials.length > 0 ? (
                                  <div>
                                    <Label className="text-sm text-blue-700 mb-2 block">
                                      Select GRN / Heat No:
                                    </Label>
                                    <Select
                                      value={material.selectedHeatNo || ""}
                                      onValueChange={(value) => {
                                        const selectedMaterial = material.availableMaterials.find(
                                          m => m.heatNo === value
                                        );
                                        if (selectedMaterial) {
                                          updateMaterialSelection(
                                            entry.id,
                                            material.bomDetailId,
                                            selectedMaterial.grnId,
                                            selectedMaterial.heatNo
                                          );
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="border-blue-300 focus:border-blue-500 focus:ring-blue-500">
                                        <SelectValue placeholder="Select GRN / Heat No" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {material.availableMaterials.map((rm) => (
                                          <SelectItem key={rm.id} value={rm.heatNo}>
                                            <div className="flex items-center justify-between w-full">
                                              <span className="font-medium">{rm.heatNo}</span>
                                              <span className="text-xs text-gray-500 ml-4">
                                                GRN: {rm.grnNumber} | Available: {rm.remainingQuantity} {rm.unit}
                                              </span>
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    
                                    {material.selectedHeatNo && (
                                      <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                                        <p className="text-xs text-green-700">
                                          Selected: {material.selectedHeatNo} | 
                                          Available: {material.availableMaterials.find(m => m.heatNo === material.selectedHeatNo)?.remainingQuantity} {material.unit}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
                                    <p className="text-sm text-yellow-700">
                                      No GRN materials available for {material.itemName}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded border border-gray-200">
                        <p className="text-sm text-gray-600">
                          No materials required for next process "{entry.nextProcessName}" or materials are still loading.
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          BOM Header ID: {entry.bomHeaderId}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Action Buttons - After entries */}
      {entries.length > 0 && (
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="border-primary-200 text-primary-700 hover:bg-primary-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || entries.length === 0}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Registering...
              </div>
            ) : (
              <>
                <ClipboardList className="h-4 w-4 mr-2" />
                Register {entries.length} Operations
              </>
            )}
          </Button>
        </div>
      )}

      {entries.length === 0 && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
          <CardContent className="p-8 text-center">
            <ClipboardList className="h-16 w-16 text-primary-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-primary-800 mb-2">No Entries to Register</h2>
            <p className="text-primary-600 mb-6">All selected work orders have been removed.</p>
            <Button 
              onClick={handleCancel} 
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to WIP Tracking
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
